import React, { FunctionComponent, useEffect } from 'react';
import { useFieldArray, Controller } from 'react-hook-form';
import { Column, Row } from '@/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faChevronRight, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { useUniqueEvent } from '../../context/uniqueevent';
import classNames from 'classnames';
import { faClock } from '@fortawesome/free-regular-svg-icons';
import { InputDatepicker, InputTimePicker } from '@/components/Input';
import { useAuth } from '@/contexts';
import { SupportedCountriesE } from '@/types/cyclone/models';
import { getGMTOffset } from '@/utils/schedule';
import dayjs from 'dayjs';

const DEFAULT_STATE = {
  date: '',
  startTime: dayjs().add(1, 'hour').startOf('hour').format('HH:mm'),
  endTime: dayjs().add(2, 'hour').startOf('hour').format('HH:mm'),
  isSave: false
};

export const Multidate: FunctionComponent = () => {
  const { control, watch, aboutErrors, setValue } = useUniqueEvent();
  const { session } = useAuth();
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'eventDates'
  });
  const eventDates = watch('eventDates');
  const [editionEnabled, setEditionEnabled] = React.useState(false);
  const country = session ? session.vendor!.country : SupportedCountriesE.ARGENTINA;
  const timezone = session ? session.vendor!.timezone : 'America/Argentina/Buenos_Aires';

  useEffect(() => {
    const isSave = eventDates.every((ticket) => ticket.isSave);
    setEditionEnabled(!isSave);
  }, [eventDates, editionEnabled]);

  useEffect(() => {
    if (eventDates.length === 1) {
      append({ date: '', startTime: eventDates[0].startTime, endTime: eventDates[0].endTime, isSave: false });
    }
  }, [eventDates, append]);

  return (
    <>
      <Column gap={20} className="w-full">
        {fields.map((item, index) => {
          const actualItems = eventDates[index];
          const handleSave = () => {
            if (!actualItems.date || !actualItems.startTime || !actualItems.endTime) {
              update(index, { ...actualItems, isSave: false });
            } else {
              update(index, { ...actualItems, isSave: true });
              setEditionEnabled(false);
            }
          };
          const handleDelete = () => {
            if (editionEnabled) {
              if (actualItems.isSave) {
                remove(index);
              } else {
                remove(index);
                setEditionEnabled(false);
              }
            } else {
              remove(index);
            }
          };
          return (
            <>
              <Row align="center" gap={5} className="w-full">
                <div
                  className={classNames(
                    'sm:w-1/2 w-[75%] border rounded-md flex sm:flex-row flex-col',
                    {
                      'bg-transparent cursor-not-allowed': actualItems.isSave,
                      'bg-[#FFF]': !actualItems.isSave
                    },
                    {
                      'border-[#F4F4F4]': !aboutErrors.eventDates,
                      'border-[#FF5A5F]': aboutErrors.eventDates
                    }
                  )}
                  key={item.id}
                >
                  <Controller
                    control={control}
                    name={`eventDates.${index}.date`}
                    render={({ field: { onChange, value } }) => (
                      <Row align="center" className="w-full sm:w-2/5 px-2 py-3">
                        <InputDatepicker
                          setDate={onChange}
                          date={value}
                          customInput={true}
                          datepickerProps={{ disabled: actualItems.isSave }}
                        />
                      </Row>
                    )}
                  />
                  <Row align="center" justify="space-between" className="w-full sm:w-3/5 px-2 sm:px-1 py-3">
                    <Controller
                      control={control}
                      name={`eventDates.${index}.startTime`}
                      render={({ field: { onChange, value } }) => {
                        const handleStartTimeChange = (newTime: string) => {
                          onChange(newTime);
                          if (!actualItems.isSave && actualItems.endTime) {
                            const currentStart = dayjs(`2000-01-01 ${value}`);
                            const currentEnd = dayjs(`2000-01-01 ${actualItems.endTime}`);
                            const duration = currentEnd.diff(currentStart, 'minutes');

                            if (duration > 0) {
                              const newEndTime = dayjs(`2000-01-01 ${newTime}`)
                                .add(duration, 'minutes')
                                .format('HH:mm');

                              // Update the end time field through the form
                              setValue(`eventDates.${index}.endTime` as any, newEndTime);
                            }
                          }
                        };

                        return (
                          <InputTimePicker
                            initialValue={value}
                            onTimeChange={handleStartTimeChange}
                            disabled={actualItems.isSave}
                          />
                        );
                      }}
                    />
                    <FontAwesomeIcon icon={faChevronRight} className="mx-2" color="#868686" />
                    <Controller
                      control={control}
                      name={`eventDates.${index}.endTime`}
                      render={({ field: { onChange, value } }) => (
                        <InputTimePicker
                          initialValue={value}
                          onTimeChange={onChange}
                          disabled={actualItems.isSave}
                        />
                      )}
                    />
                  </Row>
                </div>
                <Row align="center" gap={6} className="w-[25%] sm:w-1/2">
                  {actualItems.isSave ? (
                    <>
                      {eventDates.length > 2 && (
                        <Row
                          justify="center"
                          align="center"
                          onClick={handleDelete}
                          className="cursor-pointer w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] rounded-full bg-[#868686] hover:bg-[#FF4658] transition-colors duration-300"
                        >
                          <FontAwesomeIcon icon={faTrashCan} color="#FFF" size="1x" fixedWidth />
                        </Row>
                      )}
                    </>
                  ) : (
                    <>
                      <Row
                        justify="center"
                        align="center"
                        onClick={handleSave}
                        className="cursor-pointer w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] rounded-full bg-[#0072FB] hover:bg-[#25d366] transition-colors duration-300"
                      >
                        <FontAwesomeIcon icon={faCheck} beatFade color="#FFF" size="1x" fixedWidth />
                      </Row>
                      {eventDates.length > 2 && (
                        <Row
                          justify="center"
                          align="center"
                          onClick={handleDelete}
                          className="cursor-pointer w-9 h-9 shadow-[0px_3px_4px_0px_rgba(0,0,0,0.3)] rounded-full bg-[#868686] hover:bg-[#FF4658] transition-colors duration-300"
                        >
                          <FontAwesomeIcon icon={faTrashCan} color="#FFF" size="1x" fixedWidth />
                        </Row>
                      )}
                    </>
                  )}
                </Row>
              </Row>
            </>
          );
        })}
      </Column>
      <Row align="center" gap={10}>
        <Row
          align="center"
          gap={5}
          className="text-[#868686] text-sm border rounded border-[#F4F4F4] py-2 px-4"
        >
          <FontAwesomeIcon icon={faClock} />
          <p>
            {getGMTOffset(timezone)} {country}
          </p>
        </Row>
        {fields.length <= 30 && !editionEnabled && (
          <Row
            align="center"
            className="text-[#868686] text-sm border bg-[#FFF] rounded border-[#F4F4F4] py-2 px-4 cursor-pointer 
            hover:text-[#0072FB] transition-colors duration-200 ease-in-out"
            gap={4}
            onClick={() => {
              append(DEFAULT_STATE);
              setEditionEnabled(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <p>Agregar fecha</p>
          </Row>
        )}
      </Row>
    </>
  );
};
